import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Tooltip,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  EmojiObjects as SuggestionIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Send as SendIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  TrendingUp as TrendingIcon,
} from "@material-ui/icons";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    overflow: "hidden",
    margin: theme.spacing(0.75, 1.5),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
  },

  // Notification header/trigger
  notificationTrigger: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.info.dark,
    },
  },

  notificationTriggerWithSuggestions: {
    animation: "$suggestionPulse 2s infinite",
  },

  notificationIcon: {
    fontSize: "1.5rem",
    animation: "$iconBounce 1.5s ease-in-out infinite",
  },

  notificationContent: {
    flex: 1,
    minWidth: 0,
  },

  notificationTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    lineHeight: 1.2,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },

  notificationSubtitle: {
    fontSize: "0.75rem",
    opacity: 0.9,
  },

  badge: {
    backgroundColor: "#fff",
    color: theme.palette.info.main,
    fontWeight: 700,
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    fontSize: "0.75rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  notificationActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },

  expandButton: {
    color: "inherit",
    padding: theme.spacing(0.5),
  },

  closeButton: {
    color: "inherit",
    opacity: 0.8,
    padding: theme.spacing(0.5),
    "&:hover": {
      opacity: 1,
      backgroundColor: "rgba(255,255,255,0.2)",
    },
  },

  // Expanded list
  suggestionsContainer: {
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  suggestionsList: {
    padding: 0,
    maxHeight: 250,
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.divider,
      borderRadius: 3,
    },
  },

  suggestionItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  suggestionItemName: {
    fontWeight: 500,
    fontSize: "0.875rem",
  },

  suggestionItemDetails: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.25),
  },

  suggestionItemPrice: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    fontSize: "0.875rem",
  },

  suggestionItemReason: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },

  reasonChip: {
    height: 18,
    fontSize: "0.65rem",
  },

  suggestionActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },

  addButton: {
    minWidth: 32,
    width: 32,
    height: 32,
    padding: 0,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },

  sendButton: {
    minWidth: 32,
    width: 32,
    height: 32,
    padding: 0,
    color: "#4caf50",
    "&:hover": {
      backgroundColor: "rgba(76, 175, 80, 0.1)",
    },
  },

  // Footer actions
  footerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },

  sendAllButton: {
    textTransform: "none",
    fontWeight: 600,
  },

  // Animations
  "@keyframes suggestionPulse": {
    "0%": {
      boxShadow: `0 0 0 0 ${theme.palette.info.main}80`,
    },
    "50%": {
      boxShadow: `0 0 0 10px ${theme.palette.info.main}00`,
    },
    "100%": {
      boxShadow: `0 0 0 0 ${theme.palette.info.main}00`,
    },
  },

  "@keyframes iconBounce": {
    "0%, 100%": {
      transform: "translateY(0) rotate(0deg)",
    },
    "25%": {
      transform: "translateY(-3px) rotate(-5deg)",
    },
    "50%": {
      transform: "translateY(0) rotate(5deg)",
    },
    "75%": {
      transform: "translateY(-2px) rotate(-3deg)",
    },
  },
}));

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

/**
 * Floating notification component for customer-based sales suggestions
 * Shows animated notification when there are suggestions based on customer history
 */
const PdvSuggestionsNotification = ({
  suggestions = [],
  loading = false,
  onAddProduct,
  onSendProduct,
  onSendAll,
  onDismiss,
  onDismissAll,
  customerName,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = useCallback((e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  const handleDismissAll = useCallback((e) => {
    e.stopPropagation();
    if (onDismissAll) onDismissAll();
  }, [onDismissAll]);

  const handleAdd = useCallback((product) => {
    if (onAddProduct) onAddProduct(product);
    if (onDismiss) onDismiss(product.id);
  }, [onAddProduct, onDismiss]);

  const handleSend = useCallback((product) => {
    if (onSendProduct) onSendProduct(product);
    if (onDismiss) onDismiss(product.id);
  }, [onSendProduct, onDismiss]);

  // Don't render if no suggestions
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <Box className={classes.root}>
      {/* Notification trigger/header */}
      <Box
        className={clsx(classes.notificationTrigger, {
          [classes.notificationTriggerWithSuggestions]: suggestions.length > 0 && !expanded,
        })}
        onClick={handleToggleExpand}
      >
        <SuggestionIcon className={classes.notificationIcon} />

        <Box className={classes.notificationContent}>
          <Typography className={classes.notificationTitle}>
            Sugestoes de Venda
            <span className={classes.badge}>{suggestions.length}</span>
          </Typography>
          <Typography className={classes.notificationSubtitle}>
            {customerName
              ? `Baseado no historico de ${customerName}`
              : "Produtos que o cliente pode gostar"}
          </Typography>
        </Box>

        <Box className={classes.notificationActions}>
          <IconButton
            size="small"
            className={classes.expandButton}
            onClick={handleToggleExpand}
          >
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={handleDismissAll}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Expanded suggestions list */}
      <Collapse in={expanded}>
        <Box className={classes.suggestionsContainer}>
          <List className={classes.suggestionsList}>
            {suggestions.map((product) => (
              <ListItem key={product.id} className={classes.suggestionItem}>
                <ListItemText
                  primary={
                    <Typography className={classes.suggestionItemName}>
                      {product.name}
                    </Typography>
                  }
                  secondary={
                    <Box className={classes.suggestionItemDetails}>
                      <span className={classes.suggestionItemPrice}>
                        {formatCurrency(product.price)}
                      </span>
                      {product.reason && (
                        <Chip
                          size="small"
                          icon={<TrendingIcon style={{ fontSize: 12 }} />}
                          label={product.reason}
                          className={classes.reasonChip}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Box className={classes.suggestionActions}>
                    <Tooltip title="Enviar para cliente">
                      <IconButton
                        size="small"
                        className={classes.sendButton}
                        onClick={() => handleSend(product)}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Adicionar ao carrinho">
                      <IconButton
                        size="small"
                        className={classes.addButton}
                        onClick={() => handleAdd(product)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {/* Footer with send all action */}
          {onSendAll && (
            <Box className={classes.footerActions}>
              <Typography variant="caption" color="textSecondary">
                {suggestions.length} sugestoes disponiveis
              </Typography>
              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<SendIcon />}
                onClick={onSendAll}
                className={classes.sendAllButton}
              >
                Enviar todas
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(PdvSuggestionsNotification);
